import * as React from 'react';
import classnames from 'classnames';
import { Button, Collapse } from '@msdyn365-commerce-modules/utilities';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';

export interface IRefineCategoryTreeProps {
    label: string;
    categories: CategoryHierarchy[] | undefined;
    currentCategory: CategoryHierarchy | undefined;
    onSelectCategory: (category: CategoryHierarchy, selected: boolean) => void;
}

interface ITreeItemProps {
    baseClassName: string;
    categories: IRefineCategoryTreeProps['categories'];
    currentCategory: IRefineCategoryTreeProps['currentCategory'];
    category: CategoryHierarchy;
    level: number;
    onSelectCategory: IRefineCategoryTreeProps['onSelectCategory'];
}

function isParentToCurrentCategory(currentCategory: CategoryHierarchy | undefined, category: CategoryHierarchy): boolean {
    if (!currentCategory) {
        return false;
    }

    if (currentCategory.ParentCategory === category.RecordId) {
        return true;
    }

    // Go through category's children and children's children to see if any of them is the current category.
    for (const child of category.Children) {
        if (child.RecordId === currentCategory.RecordId) {
            return true;
        } else if (isParentToCurrentCategory(currentCategory, child)) {
            return true;
        }
    }

    return false;
}

function findCategoryInTree(tree: CategoryHierarchy[], category: CategoryHierarchy): CategoryHierarchy | undefined {
    if (!tree || tree.length === 0) {
        return undefined;
    }

    for (const item of tree) {
        if (item.RecordId === category.RecordId) {
            return item;
        } else if (item.Children && item.Children.length > 0) {
            const found = findCategoryInTree(item.Children, category);
            if (found) {
                return found;
            }
        }
    }

    return undefined;
}

const TreeItem = (props: ITreeItemProps) => {
    const { baseClassName, categories, currentCategory, category, level, onSelectCategory } = props;
    const [expanded, setExpanded] = React.useState(isParentToCurrentCategory(currentCategory, category));

    const selected = currentCategory?.RecordId === category.RecordId;
    const className = classnames(
        `${baseClassName}__tree-item`,
        {
            selected: selected || isParentToCurrentCategory(currentCategory, category),
            expanded,
            collapsed: !expanded,
            ['has-children']: category.Children.length > 0
        }
    );

    React.useEffect(() => {
        if (selected) {
            setExpanded(true);
        }
    }, [selected]);

    const onClick = () => {
        onSelectCategory(category, !selected);
    };

    return (
        <div className={className} style={{ '--lvl': level } as React.CSSProperties}>
            <div className={`${baseClassName}__tree-item__button-container`}>
                <Button
                    className={`${baseClassName}__tree-item__toggle no-style`}
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(!expanded);
                    }}
                    data-nomodalcss
                />
                <Button className={`${baseClassName}__tree-item__button no-style`} onClick={onClick}>
                    {/* <input
                        type='checkbox'
                        className={`${baseClassName}__tree-item__checkbox`}
                        aria-label='TODO'
                        checked={selected}
                        onChange={() => onSelectCategory(category, !selected)}
                        onClick={(e) => e.stopPropagation()}
                        data-nomodalcss
                    /> */}
                    <span>{category.Name}</span>
                </Button>
            </div>
            <Collapse className='ms-refine-submenu__collapse' isOpen={expanded} timeout={1} onEntered={() => {
                // Simulate the 'resize' event so that sliders gets rendered correctly.
                // If not end Slider will always start a position 0 since hidden elements have no width.
                window.dispatchEvent(new Event('resize'));
            }}>
                {category.Children.map((childCategory) => (
                    <TreeItem
                        key={childCategory.RecordId}
                        baseClassName={baseClassName}
                        categories={categories}
                        currentCategory={currentCategory}
                        category={childCategory}
                        level={level + 1}
                        onSelectCategory={onSelectCategory}
                    />
                ))}
            </Collapse>
        </div>
    );
};

const RefineCategoryTree = (props: IRefineCategoryTreeProps) => {
    const { label, categories, currentCategory } = props;
    const [expanded, setExpanded] = React.useState(true);

    const baseClassName = 'ms-refine-submenu__category-tree';
    const className = classnames('ms-refine-submenu', 'list-group', baseClassName);

    const tree = React.useMemo(() => {
        if (!categories) {
            return [];
        }

        const map: { [key: string]: CategoryHierarchy } = {};
        const roots: CategoryHierarchy[] = [];

        // 1) Index items by id
        categories?.forEach(item => {
            map[item.RecordId] = { ...item, Children: [] };
        });

        // 2) Link children to parents
        categories.forEach(item => {
            const node = map[item.RecordId];
            if (item.ParentCategory && map[item.ParentCategory]) {
                map[item.ParentCategory].Children.push(node);
            } else {
                // no parent found → it's a root
                roots.push(node);
            }
        });

        return roots;
    }, [props.categories]);

    // Find the category in tree that is the same as currentCategory.
    const parentCategory = currentCategory?.ParentCategory ? categories?.find(cat => cat.RecordId === currentCategory.ParentCategory) : currentCategory;
    let treesToShow = (parentCategory
        ? [findCategoryInTree(tree, parentCategory)].filter(Boolean)
        : []
    ) as CategoryHierarchy[];

    if (tree.length === 1) {
        // Tree has only one root, so we can show it directly.
        treesToShow = [...tree[0].Children];

        // If any other category except root (all products) is selected, only show that category tree
        if (currentCategory && currentCategory.ParentCategory) {
            treesToShow = treesToShow.filter(x => x.RecordId === currentCategory.RecordId || isParentToCurrentCategory(currentCategory, x));
        }
    }

    return (
        <div className={className}>
            <Button
                className={expanded ? 'ms-refine-sub__toggle_expanded' : 'ms-refine-sub__toggle_collapsed'}
                aria-label={label}
                onClick={() => setExpanded(!expanded)}
                aria-expanded={expanded}
            >
                {label}
            </Button>
            <Collapse className='ms-refine-submenu__collapse' isOpen={expanded} timeout={1} onEntered={() => {
                // Simulate the 'resize' event so that sliders gets rendered correctly.
                // If not end Slider will always start a position 0 since hidden elements have no width.
                window.dispatchEvent(new Event('resize'));
            }}>
                {treesToShow.filter(Boolean).map((category) => (
                    <TreeItem
                        key={category.RecordId}
                        baseClassName={baseClassName}
                        categories={categories}
                        currentCategory={currentCategory}
                        category={category}
                        level={0}
                        onSelectCategory={props.onSelectCategory}
                    />
                ))}
            </Collapse>
        </div>
    );
};

export default RefineCategoryTree;
